import React, { useEffect, useState } from 'react';
import {
    ActionIcon,
    Button,
    Flex,
    Highlight,
    Loader,
    Paper,
    ScrollArea,
    Stack,
    Table,
    TextInput,
    Title,
} from '@mantine/core';
import { IconArrowRight, IconPlus, IconSearch } from '@tabler/icons-react';
import { inject, observer } from 'mobx-react';
import { theme } from '../../../global/style/theme.tsx';
import { useTranslation } from 'react-i18next';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { useElementSize } from '@mantine/hooks';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { useGetTestKitOrders } from '../../../global/entities/TestKitOrder/queries/useGetTestKitOrders.tsx';
import { useGetTestKitOrderById } from '../../../global/entities/TestKitOrder/queries/useGetTestKitOrderById.tsx';
import { ITestKitOrderResume, TestKitOrderResume } from '../../../global/entities/TestKitOrder/TestKitOrder.ts';
import tableTools from '../../../global/helpers/tableTools.ts';

const TestKitTable: React.FC = inject()(
    observer(() => {
        const { t } = useTranslation();
        const { data, isLoading } = useGetTestKitOrders();
        const { isLoading: isLoadingGetTestKitOrderById } = useGetTestKitOrderById();
        const params = useParams<{ id: string; tabId: string }>();
        const navigate = useNavigate();
        const { ref } = useElementSize();
        const [search, setSearch] = useState('');
        const [sortedTestKit, setSortedTestKit] = useState<TestKitOrderResume[]>();
        const { sortBy, reverseSortDirection, setReverseSortDirection, setSortBy } = useTable();

        const setSorting = (field: string, data: any[]) => {
            const reversed = field === sortBy ? !reverseSortDirection : false;
            setReverseSortDirection(reversed);
            setSortBy(field);
            setSortedTestKit(tableTools.sortData(data, { sortBy: field, reversed, search }));
        };

        useEffect(() => {
            if (!data) 
                return;
            
            setSortedTestKit(data);
        }, [data, params]);

        const onClickTestKit = (id: string) => {
            navigate(`/adminDashboard/testKit/${id}`);
        };

        if (isLoading) 
            return <Loading visible={true} loadingMessage={'Loading...'} />;

        return (
            <>
                <Flex mb={'xl'} mt={'xl'} align={'center'} justify={'space-between'}>
                    <Button onClick={() => navigate('/adminDashboard/testKit')} variant="transparent">
                        <Title c="black" order={4}>
                            {t('testKit.title')}
                        </Title>
                    </Button>
                    <Button
                        onClick={() => navigate('/adminDashboard/testKit/0')}
                        leftSection={<IconPlus />}
                        styles={{ label: { align: 'center', justifyContent: 'center' } }}
                    >
                        {t('testKit.add')}
                    </Button>
                </Flex>
                <Paper ref={ref} w={'100%'}>
                    <Stack gap={'md'} h={'100%'}>
                        <TextInput
                            placeholder={t('global.search')}
                            rightSection={<IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            width={'100%'}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                        <ScrollArea w="100%" pb={'md'}>
                            <Table verticalSpacing="xs">
                                <Table.Thead>
                                    {sortedTestKit && (
                                        <Table.Tr>
                                            <Th
                                                sorted={sortBy === 'date'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('date', sortedTestKit)}
                                            >
                                                {t('global.date')}
                                            </Th>
                                            <Th
                                                sorted={sortBy === 'id'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('id', sortedTestKit)}
                                            >
                                                {t('testKit.id')}
                                            </Th>
                                            <Th
                                                sorted={sortBy === 'healthcareFacility'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('healthcareFacility', sortedTestKit)}
                                                width={150}
                                            >
                                                {t('hcf.title')}
                                            </Th>
                                            <Th
                                                sorted={sortBy === 'total'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('total', sortedTestKit)}
                                            >
                                                {t('testKit.total')}
                                            </Th>
                                            <Th
                                                sorted={sortBy === 'status'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('status', sortedTestKit)}
                                            >
                                                {t('global.status')}
                                            </Th>
                                        </Table.Tr>
                                    )}
                                </Table.Thead>
                                <Table.Tbody>
                                    {sortedTestKit &&
                                        sortedTestKit?.map((testKitOrder: ITestKitOrderResume, index: number) => {
                                            const isSearch =
                                                containsStringIgnoreCase(
                                                    [
                                                        `${testKitOrder.healthcareFacility}`,
                                                        `${testKitOrder.status}`,
                                                        `${testKitOrder.id}`,
                                                    ],
                                                    search,
                                                ) || !search;
                                            return (
                                                <Table.Tr
                                                    key={testKitOrder.id}
                                                    hidden={!isSearch}
                                                    style={{
                                                        background:
                                                            index % 2 === 1
                                                                ? undefined
                                                                : 'var(--Background-Gray-light, #E9ECEF)',
                                                    }}
                                                >
                                                    <Table.Td>
                                                        {new Date(testKitOrder.date).toISOString().split('T')[0]}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {' '}
                                                        <Highlight
                                                            highlight={search}
                                                            highlightStyles={{
                                                                color: 'var(--mantine-color-gray-9)',
                                                                fontWeight: 700,
                                                                WebkitBackgroundClip: 'text',
                                                            }}
                                                            styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                            truncate
                                                            maw={150}
                                                        >
                                                            {testKitOrder.id.toString()}
                                                        </Highlight>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Highlight
                                                            highlight={search}
                                                            highlightStyles={{
                                                                color: 'var(--mantine-color-gray-9)',
                                                                fontWeight: 700,
                                                                WebkitBackgroundClip: 'text',
                                                            }}
                                                            styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                            truncate
                                                            maw={150}
                                                        >
                                                            {testKitOrder.healthcareFacility}
                                                        </Highlight>
                                                    </Table.Td>
                                                    <Table.Td align={'right'}>
                                                        {testKitOrder.total
                                                            ? Number(testKitOrder.total).toFixed(2) + '$'
                                                            : '0.00$'}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Highlight
                                                            highlight={search}
                                                            highlightStyles={{
                                                                color: 'var(--mantine-color-gray-9)',
                                                                fontWeight: 700,
                                                                WebkitBackgroundClip: 'text',
                                                            }}
                                                            styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                            truncate
                                                        >
                                                            {testKitOrder.status}
                                                        </Highlight>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <ActionIcon variant="transparent">
                                                            {isLoadingGetTestKitOrderById ? (
                                                                <Loader
                                                                    size="sm"
                                                                    color={theme.colors?.blue?.toString()}
                                                                />
                                                            ) : (
                                                                <IconArrowRight
                                                                    color={theme.colors?.blue?.toString()}
                                                                    onClick={() =>
                                                                        onClickTestKit(testKitOrder.id.toString())
                                                                    }
                                                                />
                                                            )}
                                                        </ActionIcon>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    </Stack>
                </Paper>
            </>
        );
    }),
);

const WrappedTestKitTableTable: React.FC = () => {
    return (
        <TableContextProvider>
            <TestKitTable />
        </TableContextProvider>
    );
};

export default WrappedTestKitTableTable;
